import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  CreateRmaBody,
  Disposition,
  RmaDetail,
  RmaLine,
  RmaListResponse,
  RmaStats,
  Stage,
  TransitionRequestBody,
  TransitionResponse,
} from './types'

const RMA_KEY = ['returns-rma'] as const

export interface RmaListFilters {
  status?: Stage
  reason_code?: string
  customer_id?: number
  limit?: number
  offset?: number
}

export function useRmaList(filters: RmaListFilters = {}) {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.reason_code) params.set('reason_code', filters.reason_code)
  if (filters.customer_id != null) params.set('customer_id', String(filters.customer_id))
  params.set('limit', String(filters.limit ?? 100))
  params.set('offset', String(filters.offset ?? 0))
  const qs = params.toString()

  return useQuery({
    queryKey: [...RMA_KEY, 'list', filters],
    queryFn: () => jsonFetch<RmaListResponse>(`/api/rma?${qs}`),
    staleTime: 15_000,
  })
}

export function useRmaStats() {
  return useQuery({
    queryKey: [...RMA_KEY, 'stats'],
    queryFn: () => jsonFetch<RmaStats>('/api/rma/stats'),
    staleTime: 30_000,
  })
}

export function useRmaDetail(rmaId: string | null) {
  return useQuery({
    queryKey: [...RMA_KEY, 'detail', rmaId],
    queryFn: () => jsonFetch<RmaDetail>(`/api/rma/${rmaId}`),
    enabled: !!rmaId,
    staleTime: 5_000,
  })
}

export function useCreateRma() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateRmaBody) =>
      jsonFetch<{ rma_id: string; status: Stage }>('/api/rma', {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: RMA_KEY }),
  })
}

export interface HeaderEditVars {
  rmaId: string
  edits: Partial<{
    customer_id: number
    customer_name: string
    original_so_id: number
    original_so_name: string
    reason_code: string
    notes: string
    owner_name: string
    owner_phone: string
  }>
}

export function usePatchRmaHeader() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: HeaderEditVars) =>
      jsonFetch<RmaDetail>(`/api/rma/${vars.rmaId}`, {
        method: 'PATCH',
        body: vars.edits,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: RMA_KEY }),
  })
}

export function useAddRmaLine() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { rmaId: string; line: Partial<RmaLine> }) =>
      jsonFetch<RmaLine>(`/api/rma/${vars.rmaId}/lines`, {
        method: 'POST',
        body: vars.line,
      }),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: [...RMA_KEY, 'detail', vars.rmaId] }),
  })
}

export function usePatchRmaLine() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { rmaId: string; lineId: number; edits: Partial<RmaLine> }) =>
      jsonFetch<RmaLine>(`/api/rma/${vars.rmaId}/lines/${vars.lineId}`, {
        method: 'PATCH',
        body: vars.edits,
      }),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: [...RMA_KEY, 'detail', vars.rmaId] }),
  })
}

export function useDeleteRmaLine() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { rmaId: string; lineId: number }) =>
      jsonFetch<{ deleted: boolean }>(
        `/api/rma/${vars.rmaId}/lines/${vars.lineId}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: [...RMA_KEY, 'detail', vars.rmaId] }),
  })
}

export function useTransitionRma() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { rmaId: string; targetStage: Stage; note?: string }) => {
      const body: TransitionRequestBody = { target_stage: vars.targetStage }
      if (vars.note) body.note = vars.note
      return jsonFetch<TransitionResponse>(`/api/rma/${vars.rmaId}/transition`, {
        method: 'POST',
        body,
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RMA_KEY }),
  })
}

export function rmaPdfUrl(rmaId: string): string {
  return `/api/rma/${rmaId}/pdf`
}

// Re-exported so consumers importing from this module get the type too
// without a second import line.
export type { Disposition }
